import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField as MuiTextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import Menu from '@mui/material/Menu'
import { Button, TextField } from 'ui/baseComponents'
import Loading from 'ui/components/Loading'
import AdminPageHeader from 'ui/screens/Admin/AdminPageHeader'
import ConfirmDialog from '../components/ConfirmDialog'
import useCopyToClipboard from '../hooks/useCopyToClipboard'
import { GET_CAMPAIGN, LIST_CAMPAIGNS } from '../graphql/campaignQueries'
import {
  UPDATE_CAMPAIGN,
  DELETE_CAMPAIGN,
  SET_CAMPAIGN_CONTENT,
  UPDATE_CAMPAIGN_CONTENT,
} from '../graphql/campaignMutations'
import { useTemplateList } from '../graphql/templateQueries'
import { useTagList } from '../graphql/tagQueries'
import ContentWorkflow from './ContentWorkflow'
import { ROUTE_METADATA } from 'app/AppRoutes/RouteMetadata'

const CampaignDetails = () => {
  const { campaignId } = useParams()
  const navigate = useNavigate()

  const [campaign, setCampaign] = useState(null)
  const [error, setError] = useState(null)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [addTemplateId, setAddTemplateId] = useState('')
  const [addIntervalDays, setAddIntervalDays] = useState('0')
  const [addIntervalHours, setAddIntervalHours] = useState('0')
  const { copied, copy: handleCopyId } = useCopyToClipboard(campaignId)

  // Inline editing state
  const [editingName, setEditingName] = useState(false)
  const [tempName, setTempName] = useState('')
  const [editingDescription, setEditingDescription] = useState(false)
  const [tempDescription, setTempDescription] = useState('')
  const [editingPriority, setEditingPriority] = useState(false)
  const [tempPriority, setTempPriority] = useState('')
  const [editingTag, setEditingTag] = useState(false)
  const [tempTagId, setTempTagId] = useState('')

  const { data: templatesData } = useTemplateList()
  const { data: tagsData } = useTagList()
  const templates = templatesData?.contentTemplateList?.templates || []
  const tags = tagsData?.tags || []
  const tagMap = Object.fromEntries(tags.map((t) => [t.id, t.tag]))

  const refetchConfig = {
    refetchQueries: [
      { query: GET_CAMPAIGN, variables: { id: campaignId } },
      { query: LIST_CAMPAIGNS, variables: { limit: 100 } },
    ],
  }

  const [fetchCampaign, { loading }] = useLazyQuery(GET_CAMPAIGN, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.campaign) setCampaign(data.campaign)
    },
    onError: () => setError('Failed to load campaign'),
  })

  const [updateCampaign] = useMutation(UPDATE_CAMPAIGN, {
    refetchQueries: [{ query: LIST_CAMPAIGNS, variables: { limit: 100 } }],
    onError: () => setError('Failed to update campaign'),
  })

  const [deleteCampaign, { loading: deleting }] = useMutation(DELETE_CAMPAIGN, {
    refetchQueries: [{ query: LIST_CAMPAIGNS, variables: { limit: 100 } }],
    onCompleted: () => navigate(ROUTE_METADATA.adminOutreachCampaigns.path),
    onError: () => setError('Failed to delete campaign'),
  })

  const [setCampaignContent] = useMutation(SET_CAMPAIGN_CONTENT, refetchConfig)
  const [updateContent] = useMutation(UPDATE_CAMPAIGN_CONTENT, refetchConfig)

  useEffect(() => {
    if (campaignId) {
      fetchCampaign({ variables: { id: campaignId } })
    }
  }, [campaignId, fetchCampaign])

  const autoSave = useCallback(
    async (field, value) => {
      setError(null)
      const input = { id: campaignId }
      input[field] = value
      try {
        await updateCampaign({ variables: { input } })
        setCampaign((prev) => (prev ? { ...prev, [field]: value } : prev))
      } catch {
        setError('Failed to update campaign')
      }
    },
    [campaignId, updateCampaign],
  )

  const handleDelete = useCallback(async () => {
    setError(null)
    await deleteCampaign({ variables: { input: { id: campaignId } } })
    setDeleteDialogOpen(false)
  }, [campaignId, deleteCampaign])

  // Name editing
  const handleNameEdit = useCallback(() => {
    setTempName(campaign?.name || '')
    setEditingName(true)
  }, [campaign])

  const handleNameSave = useCallback(async () => {
    if (tempName.trim()) {
      await autoSave('name', tempName.trim())
      setEditingName(false)
    }
  }, [tempName, autoSave])

  const handleNameCancel = useCallback(() => {
    setEditingName(false)
  }, [])

  // Description editing
  const handleDescriptionEdit = useCallback(() => {
    setTempDescription(campaign?.description || '')
    setEditingDescription(true)
  }, [campaign])

  const handleDescriptionSave = useCallback(async () => {
    await autoSave('description', tempDescription)
    setEditingDescription(false)
  }, [tempDescription, autoSave])

  const handleDescriptionCancel = useCallback(() => {
    setEditingDescription(false)
  }, [])

  // Priority editing
  const handlePriorityEdit = useCallback(() => {
    setTempPriority(String(campaign?.priority ?? 0))
    setEditingPriority(true)
  }, [campaign])

  const handlePrioritySave = useCallback(async () => {
    const val = parseInt(tempPriority, 10)
    if (!isNaN(val)) {
      await autoSave('priority', val)
    }
    setEditingPriority(false)
  }, [tempPriority, autoSave])

  const handlePriorityCancel = useCallback(() => {
    setEditingPriority(false)
  }, [])

  // Content handlers. Existing steps carry their id so the backend updates them
  // in place instead of retiring them; outreach history stays linked to them.
  const contentToInput = useCallback(
    (items) =>
      items.map((item) => ({
        ...(item.id ? { id: item.id } : {}),
        templateId: item.templateId,
        intervalHours: item.intervalHours,
      })),
    [],
  )

  const saveContent = useCallback(
    async (items) => {
      setError(null)
      try {
        await setCampaignContent({
          variables: {
            input: {
              campaignId,
              content: contentToInput(items),
            },
          },
        })
      } catch {
        setError('Failed to save campaign content')
      }
    },
    [campaignId, setCampaignContent, contentToInput],
  )

  const handleAddContent = useCallback(async () => {
    if (!addTemplateId) return
    const sorted = [...(campaign?.campaignContent || [])].sort(
      (a, b) => a.sequence - b.sequence,
    )
    const days = parseInt(addIntervalDays, 10) || 0
    const hours = parseInt(addIntervalHours, 10) || 0
    const newItem = {
      templateId: addTemplateId,
      intervalHours: days * 24 + hours,
    }
    await saveContent([...sorted, newItem])
    setAddDialogOpen(false)
    setAddTemplateId('')
    setAddIntervalDays('0')
    setAddIntervalHours('0')
  }, [addTemplateId, addIntervalDays, addIntervalHours, campaign, saveContent])

  const handleUpdateContent = useCallback(
    async (contentId, updates) => {
      setError(null)
      try {
        await updateContent({
          variables: { input: { id: contentId, ...updates } },
        })
      } catch {
        setError('Failed to update content')
      }
    },
    [updateContent],
  )

  const handleRemoveContent = useCallback(
    async (contentId) => {
      const sorted = [...(campaign?.campaignContent || [])].sort(
        (a, b) => a.sequence - b.sequence,
      )
      await saveContent(sorted.filter((item) => item.id !== contentId))
    },
    [campaign, saveContent],
  )

  const handleReorder = useCallback(
    async (reorderedContent) => {
      await saveContent(reorderedContent)
    },
    [saveContent],
  )

  if (loading && !campaign) return <Loading />

  if (!campaign && !loading) {
    return <Alert severity="error">Campaign not found</Alert>
  }

  if (!campaign) return null

  const sortedContent = [...(campaign.campaignContent || [])].sort(
    (a, b) => a.sequence - b.sequence,
  )

  return (
    <>
      <AdminPageHeader>
        <Typography variant="h5" component="h1">
          Campaign Details
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            type="secondary"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() =>
              navigate(
                `${ROUTE_METADATA.adminOutreachCampaigns.path}/${campaignId}`,
              )
            }
          >
            Back to Dashboard
          </Button>
          <IconButton
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            sx={{ '& .MuiList-root': { py: 1 } }}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null)
                setDeleteDialogOpen(true)
              }}
              sx={{ px: 3, py: 1.5, minWidth: 250, color: 'error.main' }}
            >
              Delete Campaign
            </MenuItem>
          </Menu>
        </Box>
      </AdminPageHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Editable header */}
      <Card
        elevation={0}
        sx={{ mb: 3, backgroundColor: 'transparent', px: 3, py: 2 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 4,
          }}
        >
          <Box sx={{ flex: 1 }}>
            {/* Name row with active toggle and priority */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}
            >
              {editingName ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <MuiTextField
                    value={tempName}
                    onChange={(e) => setTempName(e.target.value)}
                    size="small"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleNameSave()
                      if (e.key === 'Escape') handleNameCancel()
                    }}
                    sx={{ maxWidth: 500 }}
                    variant="standard"
                    InputProps={{
                      sx: { fontSize: '2.125rem', fontWeight: 400 },
                    }}
                  />
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleNameSave}
                  >
                    <CheckIcon />
                  </IconButton>
                  <IconButton size="small" onClick={handleNameCancel}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              ) : (
                <Typography
                  variant="h4"
                  component="h2"
                  onClick={handleNameEdit}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.7 },
                  }}
                >
                  {campaign.name}
                </Typography>
              )}

              {/* Active toggle */}
              <Box
                onClick={() => autoSave('active', !campaign.active)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 16,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: campaign.active
                      ? 'success.main'
                      : 'error.main',
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {campaign.active ? 'Active' : 'Inactive'}
                </Typography>
              </Box>

              {/* Priority */}
              {editingPriority ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <MuiTextField
                    value={tempPriority}
                    onChange={(e) => setTempPriority(e.target.value)}
                    size="small"
                    autoFocus
                    type="number"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handlePrioritySave()
                      if (e.key === 'Escape') handlePriorityCancel()
                    }}
                    sx={{ width: 80 }}
                    variant="standard"
                    InputProps={{
                      sx: { fontSize: 14, textAlign: 'center' },
                    }}
                  />
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handlePrioritySave}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={handlePriorityCancel}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  onClick={handlePriorityEdit}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 16,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Priority: {campaign.priority}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* ID and Tag row */}
            <Box
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 4, mb: 1 }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  ID
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '& .copy-id-icon': { opacity: 0 },
                    '&:hover .copy-id-icon': { opacity: 1 },
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {campaignId}
                  </Typography>
                  <IconButton
                    className="copy-id-icon"
                    size="small"
                    onClick={handleCopyId}
                    sx={{ p: 0, transition: 'opacity 0.15s' }}
                    title="Copy campaign ID"
                    aria-label="Copy campaign ID"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                  {copied && (
                    <Typography
                      variant="body2"
                      color="success.main"
                      sx={{ ml: 0.5 }}
                    >
                      Copied!
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Tag
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {editingTag ? (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Autocomplete
                        value={tags.find((t) => t.id === tempTagId) || null}
                        onChange={(e, newValue) => {
                          if (newValue) setTempTagId(newValue.id)
                        }}
                        options={tags}
                        getOptionLabel={(option) => option.tag || option.id}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        renderInput={(params) => (
                          <MuiTextField
                            {...params}
                            size="small"
                            autoFocus
                            placeholder="Search tags..."
                          />
                        )}
                        sx={{ width: 400 }}
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          if (tempTagId) {
                            autoSave('tagId', tempTagId)
                          }
                          setEditingTag(false)
                        }}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setEditingTag(false)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box
                      onClick={() => {
                        setTempTagId(campaign.tagId)
                        setEditingTag(true)
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        '& .tag-link-icon': { opacity: 0 },
                        '&:hover .tag-link-icon': { opacity: 1 },
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.7 },
                        }}
                      >
                        {tagMap[campaign.tagId] || campaign.tagId}
                      </Typography>
                      <IconButton
                        className="tag-link-icon"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(
                            `${ROUTE_METADATA.adminOutreachTags.path}/${campaign.tagId}`,
                          )
                        }}
                        title="Open tag details"
                        aria-label="Open tag details"
                        sx={{ p: 0, transition: 'opacity 0.15s' }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Description */}
            <Typography variant="caption" color="text.secondary">
              Description
            </Typography>
            <Box sx={{ mb: 2 }}>
              {editingDescription ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <MuiTextField
                    value={tempDescription}
                    onChange={(e) => setTempDescription(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.metaKey)
                        handleDescriptionSave()
                      if (e.key === 'Escape') handleDescriptionCancel()
                    }}
                    placeholder="Add a description..."
                    multiline
                    rows={2}
                    fullWidth
                    autoFocus
                    size="small"
                    sx={{ maxWidth: 600 }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}
                  >
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={handleDescriptionSave}
                    >
                      <CheckIcon />
                    </IconButton>
                    <IconButton size="small" onClick={handleDescriptionCancel}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  onClick={handleDescriptionEdit}
                  sx={{
                    cursor: 'pointer',
                    fontStyle: campaign.description ? 'normal' : 'italic',
                    '&:hover': { opacity: 0.7 },
                  }}
                >
                  {campaign.description || 'Add a description...'}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Right side: Dates */}
          <Box sx={{ display: 'flex', gap: 2, pt: 1 }}>
            <MuiTextField
              label="Start Date"
              type="date"
              value={campaign.startDate || ''}
              onChange={(e) => autoSave('startDate', e.target.value || null)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 160 }}
            />
            <MuiTextField
              label="End Date"
              type="date"
              value={campaign.endDate || ''}
              onChange={(e) => autoSave('endDate', e.target.value || null)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 160 }}
            />
          </Box>
        </Box>
      </Card>

      {/* Content workflow */}
      <Card elevation={0}>
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Typography variant="h6">
              Content Sequence ({sortedContent.length})
            </Typography>
            <Button
              type="primary"
              size="small"
              onClick={() => setAddDialogOpen(true)}
            >
              Add Step
            </Button>
          </Box>
          <ContentWorkflow
            content={sortedContent}
            onUpdateContent={handleUpdateContent}
            onRemoveContent={handleRemoveContent}
            onReorder={handleReorder}
          />
        </CardContent>
      </Card>

      {/* Add content dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Content Step</DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pt: '8px !important',
          }}
        >
          <Autocomplete
            value={templates.find((t) => t.id === addTemplateId) || null}
            onChange={(e, newValue) => setAddTemplateId(newValue?.id || '')}
            options={templates}
            getOptionLabel={(option) => `${option.name} (${option.channel})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                label="Template"
                size="small"
                placeholder="Search templates..."
              />
            )}
          />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Days"
              value={addIntervalDays}
              onChange={(e) => setAddIntervalDays(e.target.value)}
              type="number"
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Hours"
              value={addIntervalHours}
              onChange={(e) => setAddIntervalHours(e.target.value)}
              type="number"
              size="small"
              sx={{ flex: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button type="secondary" onClick={() => setAddDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleAddContent}
            disabled={!addTemplateId}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Campaign"
        message="Are you sure you want to delete this campaign and all its content?"
        confirmLabel="Delete"
        loading={deleting}
      />
    </>
  )
}

export default CampaignDetails
